import React, { useState, useEffect } from 'react';
import { useProfileViews } from '../hooks/useProfileViews';
import { useAuth } from '../contexts/AuthContext';

const ProfileViewNotification = () => {
    const { user } = useAuth();
    const { getProfileViews } = useProfileViews();
    const [recentViews, setRecentViews] = useState([]);
    const [showNotification, setShowNotification] = useState(false);
    const [lastChecked, setLastChecked] = useState(null);

    useEffect(() => {
        if (user?.id) {
            checkForNewViews();
            // Check for new views every 30 seconds
            const interval = setInterval(checkForNewViews, 30000);
            return () => clearInterval(interval);
        }
    }, [user]);

    const checkForNewViews = async () => {
        try {
            const views = await getProfileViews(user.id, 5);
            const newViews = views.filter(view => {
                if (!lastChecked) return true;
                return new Date(view.view_timestamp) > new Date(lastChecked);
            });

            if (newViews.length > 0) {
                setRecentViews(newViews);
                setShowNotification(true);
                
                // Auto-hide notification after 5 seconds
                setTimeout(() => {
                    setShowNotification(false);
                }, 5000);
            }

            setLastChecked(new Date().toISOString());
        } catch (error) {
            console.error('Error checking for new profile views:', error);
        }
    };

    const getViewerName = (view) => {
        if (view.viewer?.user_metadata) {
            const metadata = view.viewer.user_metadata;
            if (metadata.first_name && metadata.last_name) {
                return `${metadata.first_name} ${metadata.last_name}`;
            }
            if (metadata.companyName) {
                return metadata.companyName;
            }
        }
        return view.viewer?.email || 'Someone';
    };

    const formatTimeAgo = (timestamp) => {
        const now = new Date();
        const viewTime = new Date(timestamp);
        const diffInSeconds = Math.floor((now - viewTime) / 1000);

        if (diffInSeconds < 60) return 'just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        return `${Math.floor(diffInSeconds / 86400)}d ago`;
    };

    if (!showNotification || recentViews.length === 0) {
        return null;
    }

    return (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 animate-slide-in">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center mb-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                            <h3 className="text-sm font-semibold text-gray-900">Profile Views</h3>
                        </div>
                        
                        <div className="space-y-2">
                            {recentViews.slice(0, 3).map((view, index) => (
                                <div key={index} className="text-sm">
                                    <span className="text-gray-900 font-medium">
                                        {getViewerName(view)}
                                    </span>
                                    <span className="text-gray-600 ml-1">
                                        viewed your profile
                                    </span>
                                    <span className="text-gray-400 ml-1">
                                        {formatTimeAgo(view.view_timestamp)}
                                    </span>
                                </div>
                            ))}
                            
                            {recentViews.length > 3 && (
                                <div className="text-xs text-gray-500">
                                    +{recentViews.length - 3} more views
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <button
                        onClick={() => setShowNotification(false)}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileViewNotification;
